// 検索パネルのフォームデータから静的URLを組み立てるユーティリティ
//
// Ruby側の dispatched_facilities_path / build_static_url_path と同じURL生成ロジックを
// JS側に移植したもの。セグメント順序は固定で、NursingFacility::StaticUrl::Parser と対応する。
//
// 静的URLに変換できないパラメータ（keyword等）が含まれる場合は null を返し、
// 呼び出し元は従来通り /search/ にフォールバックする。

// オプションフラグの固定順序（NursingFacility::StaticUrl::Parser::OPTION_FLAGS と対応）
const OPTION_FLAGS = [
  'with_review',
  'vacancies_exist',
  'welfare_acceptable',
  'no_joint_surety_required',
  'dementia_acceptable',
]

// 静的URLに変換不可能なパラメータ（これらが存在する場合は /search/ にフォールバック）
const NON_STATIC_PARAMS = [
  'keyword',
  'walking_time',
  'exclude_facility_id',
  'plans_present',
  'excluded_city_id',
  'excluded_station_id',
  'route_id',
  'route_ids[]',
  // 注: sort_order は静的URLパスに含まれないため、検索パネルから静的URLに遷移すると
  // 並び替え設定はリセットされる（仕様）。静的URLはデフォルト順で表示される。
]

/**
 * FormDataから静的URLを組み立てる
 * @param {FormData} formData - 検索フォームのFormData
 * @returns {string|null} 静的URL。変換不可能な場合は null
 */
export function buildStaticUrl (formData) {
  // 静的URLに変換不可能なパラメータが含まれている場合はフォールバック
  for (const key of NON_STATIC_PARAMS) {
    if (formData.get(key)) return null
  }

  // 都道府県の取得（必須）
  const prefectureIds = getIds(formData, 'prefecture_ids[]')
  const prefectureId = formData.get('prefecture_id')

  // 都道府県がない場合は静的URLを生成できない
  if (prefectureIds.length === 0 && !prefectureId) return null

  const parts = []

  // 都道府県セグメント
  if (prefectureIds.length > 0) {
    parts.push(`prefectures/${sortNumeric(prefectureIds).join(',')}`)
  } else {
    parts.push(`prefectures/${prefectureId}`)
  }

  // エリア軸（固定順序: 政令指定都市 → 市区町村 → 駅）
  appendIdSegment(parts, formData, 'ordinance_designated_cities', 'ordinance_designated_city_ids[]', 'ordinance_designated_city_id')
  appendIdSegment(parts, formData, 'cities', 'city_ids[]', 'city_id')
  appendIdSegment(parts, formData, 'stations', 'station_ids[]', 'station_id')

  // 条件軸（固定順序: カテゴリ → 介護度 → 医療ケア → 特徴）
  appendIdSegment(parts, formData, 'categories', 'category_ids[]', 'category_id')
  appendIdSegment(parts, formData, 'care_levels', 'care_level_ids[]', 'care_level_id')
  appendIdSegment(parts, formData, 'medical_cares', 'medical_care_ids[]', 'medical_care_id')
  appendIdSegment(parts, formData, 'features', 'feature_content_ids[]', 'feature_content_id')

  // オプション軸（ONのフラグのみカンマ区切り、固定順序）
  const activeOptions = OPTION_FLAGS.filter(flag => {
    const val = formData.get(flag)
    return val && val !== '0' && val !== 'false'
  })
  if (activeOptions.length > 0) {
    parts.push(`options/${activeOptions.join(',')}`)
  }

  // 料金軸（円 → 万円変換）
  appendPriceSegment(parts, formData, 'mon_min', 'monthly_fee_min')
  appendPriceSegment(parts, formData, 'mon_max', 'monthly_fee_max')
  appendPriceSegment(parts, formData, 'adm_min', 'admission_fee_min')
  appendPriceSegment(parts, formData, 'adm_max', 'admission_fee_max')

  // 末尾は /facilities/
  parts.push('facilities/')

  return '/' + parts.join('/')
}

/**
 * FormDataから複数IDを取得する
 */
function getIds (formData, pluralKey) {
  return formData.getAll(pluralKey).filter(v => v && v !== '')
}

/**
 * IDを数値昇順でソートする
 */
function sortNumeric (ids) {
  return [...ids].sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
}

/**
 * IDセグメントを追加する（複数ID → カンマ区切り昇順、単一ID → そのまま）
 */
function appendIdSegment (parts, formData, segmentName, pluralKey, singularKey) {
  const ids = getIds(formData, pluralKey)
  if (ids.length > 0) {
    parts.push(`${segmentName}/${sortNumeric(ids).join(',')}`)
  } else {
    const singleId = formData.get(singularKey)
    if (singleId && singleId !== '') {
      parts.push(`${segmentName}/${singleId}`)
    }
  }
}

/**
 * 料金セグメントを追加する（円 → 万円変換）
 */
function appendPriceSegment (parts, formData, urlKey, paramKey) {
  const value = formData.get(paramKey)
  if (value && value !== '' && parseInt(value, 10) > 0) {
    parts.push(`${urlKey}=${Math.floor(parseInt(value, 10) / 10000)}`)
  }
}

/**
 * フォームデータの都道府県パラメータを、選択済み市区町村の都道府県に合わせて調整する
 *
 * クロス都道府県選択（現在の都道府県ページとは別の都道府県の市区町村を選択）に対応する。
 * 市区町村チェックボックスは ul[data-value-pref="{prefecture_id}"] 内に描画されるため、
 * closest('[data-value-pref]') で親要素の都道府県IDを取得する。
 *
 * 都道府県タブを切り替えると前のタブのチェックボックスが DOM から消えるため、
 * preserveCheckedSelections() で保存された hidden input（data-value-pref 付き）も
 * 併せて参照することで、複数都道府県にまたがる選択を正しく判定する。
 *
 * 注: station は hidden field 由来で常に現在ページの都道府県と整合するため、クロス都道府県判定の対象外。
 *
 * @param {FormData} formData - 検索フォームのFormData（内容が変更されます）
 * @param {Element[]} cityElements - 市区町村チェックボックス要素の配列
 * @param {Element[]} odcElements - 政令指定都市チェックボックス要素の配列
 * @param {Element[]} preservedCityElements - タブ切り替え時に保存された市区町村 hidden input の配列
 * @param {Element[]} preservedOdcElements - タブ切り替え時に保存された政令指定都市 hidden input の配列
 * @returns {FormData} 調整後のFormData（引数と同じオブジェクト）
 */
export function prepareStaticUrlFormData (formData, cityElements, odcElements, preservedCityElements = [], preservedOdcElements = []) {
  const currentPrefId = formData.get('prefecture_id')

  // チェック済み市区町村・政令指定都市が属する都道府県IDを収集
  const checkedPrefIds = [...cityElements, ...odcElements]
    .filter(el => el.checked)
    .map(el => el.closest('[data-value-pref]')?.dataset?.valuePref)

  // 別タブに切り替えた際に preserveCheckedSelections() で保存された hidden input からも収集
  const preservedPrefIds = [...preservedCityElements, ...preservedOdcElements]
    .filter(el => el.dataset?.valuePref)
    .map(el => el.dataset.valuePref)

  // 重複排除して全都道府県IDを統合
  const allPrefIds = [...checkedPrefIds, ...preservedPrefIds]
    .filter((v, i, arr) => v && arr.indexOf(v) === i)

  if (allPrefIds.length === 1 && allPrefIds[0] !== currentPrefId) {
    // 1つの別都道府県のみ選択 → prefecture_id を上書き
    // 例: 東京ページで神奈川の市区町村を選択 → prefecture_id を神奈川IDに変更して静的URLを生成
    formData.set('prefecture_id', allPrefIds[0])
  } else if (allPrefIds.length >= 2) {
    // 複数都道府県にまたがる選択 → prefecture_ids[] に変換して静的URLを生成
    // 例: 東京+神奈川の市区町村を選択 → /prefectures/13,14/cities/.../facilities/
    formData.delete('prefecture_id')
    allPrefIds.forEach(prefId => formData.append('prefecture_ids[]', prefId))
  }

  return formData
}
